import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Inbox,
  Loader2,
  RefreshCw,
  Send,
  Star,
  UserRound,
} from "lucide-react";
import {
  fetchSupportTickets,
  ticketCategories,
  updateSupportTicket,
} from "../../Support/supportTickets";

const statusOptions = ["Open", "In Progress", "Resolved"];

const getTicketId = (ticket) => ticket?._id || ticket?.id || ticket?.ticketNumber;

const getCategoryLabel = (category) => {
  if (category === "generation-support") return "Study & Practice Help";
  return (
    ticketCategories.find((item) => item.value === category || item.label === category)?.label ||
    category ||
    "Support"
  );
};

const getInitials = (name) =>
  String(name || "User")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const statusClass = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "resolved") return "bg-emerald-50 text-emerald-700 ring-emerald-100";
  if (key === "in progress") return "bg-sky-50 text-sky-700 ring-sky-100";
  return "bg-amber-50 text-amber-700 ring-amber-100";
};

const TicketThreadMessage = ({ message }) => {
  const isAdmin = message.authorType === "admin";

  return (
    <div className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[82%] rounded-lg px-4 py-3 shadow-sm ${
          isAdmin ? "bg-slate-950 text-white" : "border border-slate-200 bg-white text-slate-800"
        }`}
      >
        <p className={`text-xs font-semibold ${isAdmin ? "text-slate-300" : "text-sky-600"}`}>
          {message.authorName || (isAdmin ? "Admin" : "Learner")}
        </p>
        <p className="mt-1 text-sm leading-6">{message.message}</p>
      </div>
    </div>
  );
};

export default function AdminSupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [activeTab, setActiveTab] = useState("Open");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const ticketStats = useMemo(
    () =>
      statusOptions.map((status) => ({
        status,
        count: tickets.filter((ticket) => ticket.status === status).length,
      })),
    [tickets]
  );

  const filteredTickets = useMemo(
    () => tickets.filter((ticket) => (ticket.status || "Open") === activeTab),
    [activeTab, tickets]
  );

  const selectedTicket = useMemo(
    () => filteredTickets.find((ticket) => getTicketId(ticket) === selectedTicketId) || filteredTickets[0],
    [filteredTickets, selectedTicketId]
  );

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const ticketList = await fetchSupportTickets({ admin: true });
      setTickets(ticketList);
    } catch (fetchError) {
      setError(fetchError.message || "Unable to load support tickets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
    const refreshTimer = setInterval(loadTickets, 30000);

    return () => clearInterval(refreshTimer);
  }, [loadTickets]);

  useEffect(() => {
    if (!filteredTickets.length) {
      if (selectedTicketId) setSelectedTicketId("");
      return;
    }

    const selectedTicketExists = filteredTickets.some((ticket) => getTicketId(ticket) === selectedTicketId);
    if (!selectedTicketExists) {
      setSelectedTicketId(getTicketId(filteredTickets[0]));
    }
  }, [filteredTickets, selectedTicketId]);

  const applyTicketUpdate = (updatedTicket) => {
    setTickets((current) => {
      const updatedId = getTicketId(updatedTicket);
      const exists = current.some((ticket) => getTicketId(ticket) === updatedId);
      const nextTickets = exists
        ? current.map((ticket) => (getTicketId(ticket) === updatedId ? updatedTicket : ticket))
        : [updatedTicket, ...current];

      return [...nextTickets].sort(
        (a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
      );
    });
    setSelectedTicketId(getTicketId(updatedTicket));
  };

  const handleStatusChange = async (status) => {
    if (!selectedTicket || selectedTicket.status === status) return;

    setSaving(true);
    setError("");

    try {
      const updatedTicket = await updateSupportTicket(getTicketId(selectedTicket), { status });
      applyTicketUpdate(updatedTicket);
      setActiveTab(status);
    } catch (statusError) {
      setError(statusError.message || "Unable to update ticket status");
    } finally {
      setSaving(false);
    }
  };

  const handleReply = async () => {
    if (!selectedTicket || !reply.trim()) return;

    setSaving(true);
    setError("");

    try {
      const updatedTicket = await updateSupportTicket(getTicketId(selectedTicket), {
        reply: reply.trim(),
      });
      applyTicketUpdate(updatedTicket);
      setActiveTab(updatedTicket.status || activeTab);
      setReply("");
    } catch (replyError) {
      setError(replyError.message || "Unable to send reply");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-col gap-4 border-b border-slate-100 pb-5 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-950">
            <Inbox className="h-5 w-5 text-sky-600" />
            Support Tickets
          </h2>
          <p className="text-sm text-slate-500">Learner support tickets from the support page</p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="grid grid-cols-3 gap-2">
            {ticketStats.map((item) => (
              <div key={item.status} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase text-slate-500">{item.status}</p>
                <p className="text-lg font-bold text-slate-950">{item.count}</p>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={loadTickets}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(280px,420px),1fr]">
        <div className="rounded-lg border border-slate-200">
          <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
            <p className="text-sm font-bold text-slate-950">Inbox</p>
            <p className="text-xs text-slate-500">{tickets.length} tickets</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {ticketStats.map((item) => (
                <button
                  key={item.status}
                  type="button"
                  onClick={() => setActiveTab(item.status)}
                  className={`rounded-lg px-2.5 py-2 text-xs font-semibold transition ${
                    activeTab === item.status
                      ? "bg-sky-600 text-white shadow-sm"
                      : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <span className="block truncate">{item.status}</span>
                  <span className={activeTab === item.status ? "text-sky-100" : "text-slate-400"}>
                    {item.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[520px] overflow-y-auto p-3">
            {loading ? (
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-4 text-sm font-semibold text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin text-sky-600" />
                Loading support tickets...
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="rounded-lg bg-slate-50 p-6 text-center text-sm text-slate-500">
                No {activeTab.toLowerCase()} tickets.
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTickets.map((ticket) => {
                  const ticketId = getTicketId(ticket);
                  const isSelected = getTicketId(selectedTicket) === ticketId;

                  return (
                    <button
                      type="button"
                      key={ticketId}
                      onClick={() => setSelectedTicketId(ticketId)}
                      className={`w-full rounded-lg border p-4 text-left transition ${
                        isSelected
                          ? "border-sky-200 bg-sky-50"
                          : "border-slate-200 bg-white hover:border-sky-200 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-sm font-bold text-slate-700">
                          {getInitials(ticket.userName)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="truncate font-semibold text-slate-950">{ticket.userName || "Learner"}</p>
                            <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusClass(ticket.status)}`}>
                              {ticket.status || "Open"}
                            </span>
                          </div>
                          <p className="mt-1 truncate text-sm text-slate-500">{ticket.subject}</p>
                          <p className="mt-2 truncate text-xs font-semibold text-sky-700">
                            {ticket.ticketNumber || "Ticket"} - {getCategoryLabel(ticket.category)}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="min-h-[520px] rounded-lg border border-slate-200 bg-slate-50 p-4">
          {selectedTicket ? (
            <div className="flex h-full flex-col">
              <div className="mb-4 flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase text-sky-600">
                    {selectedTicket.ticketNumber || "Support ticket"}
                  </p>
                  <h3 className="mt-1 truncate text-xl font-bold text-slate-950">{selectedTicket.subject}</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                      {getCategoryLabel(selectedTicket.category)}
                    </span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusClass(selectedTicket.status)}`}>
                      {selectedTicket.status || "Open"}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                      <Clock3 className="h-3.5 w-3.5" />
                      {formatDate(selectedTicket.updatedAt || selectedTicket.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {statusOptions.map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => handleStatusChange(status)}
                      disabled={saving}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${
                        selectedTicket.status === status
                          ? "bg-slate-950 text-white"
                          : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {status === "Resolved" && <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />}
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4 grid gap-3 sm:grid-cols-4">
                {[
                  ["User", selectedTicket.userName || "Learner"],
                  ["Email", selectedTicket.userEmail || "Not available"],
                  ["Plan Status", selectedTicket.subscriptionStatus || "Not tracked"],
                  ["Rating", selectedTicket.rating?.score ? `${selectedTicket.rating.score}/5` : "Not rated"],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-slate-200 bg-white p-3">
                    <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
                    <p className="mt-1 flex items-center gap-1 truncate text-sm font-semibold text-slate-950">
                      {label === "Rating" && selectedTicket.rating?.score ? (
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      ) : null}
                      {value}
                    </p>
                  </div>
                ))}
              </div>

              {selectedTicket.rating?.comment && (
                <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50 p-3">
                  <p className="text-xs font-semibold uppercase text-amber-700">User rating note</p>
                  <p className="mt-1 text-sm text-slate-700">{selectedTicket.rating.comment}</p>
                </div>
              )}

              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-lg border border-slate-200 bg-white p-4">
                {(selectedTicket.messages?.length
                  ? selectedTicket.messages
                  : [
                      {
                        authorType: "user",
                        authorName: selectedTicket.userName,
                        message: selectedTicket.message,
                      },
                    ]
                ).map((message, index) => (
                  <TicketThreadMessage key={message._id || message.id || index} message={message} />
                ))}
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <input
                  type="text"
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") handleReply();
                  }}
                  className="h-11 flex-1 rounded-lg border border-slate-200 bg-white px-4 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  placeholder="Type admin reply..."
                />
                <button
                  type="button"
                  onClick={handleReply}
                  disabled={saving || !reply.trim()}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-sky-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send
                </button>
              </div>
            </div>
          ) : (
            <div className="flex min-h-[420px] flex-col items-center justify-center text-center text-sm text-slate-500">
              <UserRound className="mb-3 h-8 w-8 text-slate-300" />
              Select a ticket to review the conversation.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
